// Cybo Autonomy (#964) — UI mirror of the server's channel/cybo dial vocabulary
// + the effective-agency resolver. packages/ui never imports packages/server
// code (separate package boundary), so the persisted vocabulary and the pure
// min() resolver are vendored here — same pattern as provider-catalog.ts /
// daemon-scopes.ts.
//
// KEEP IN SYNC WITH SERVER:
//   packages/server/src/server/cyborg/autonomy-types.ts (AUTONOMY_LEVELS,
//   AUTONOMY_RANK, CHANNEL_REGIMES, AUTONOMY_PRESETS, behaviorModeToLevel)
//   packages/server/src/server/cyborg/autonomy-dials.ts (resolveEffectiveAutonomy,
//   isValidChannelMaxAutonomyLevel)
//
// Two independent dials: a per-cybo `autonomyLevel` (the cybo's disposition,
// travels with it across channels) and a per-channel `regime` + `maxAutonomyLevel`
// ceiling (the room's social contract). A cybo's EFFECTIVE agency in a channel is
// the floor of the two, by rank — resolveEffectiveAutonomy() below.
//
// PURE: zero I/O, zero app-state deps — safe to import from any component or a
// plain vitest file.

export const AUTONOMY_LEVELS = ["L0", "L1", "L2", "L3", "L4", "L5"] as const;
export type AutonomyLevel = (typeof AUTONOMY_LEVELS)[number];

export const AUTONOMY_RANK: Record<AutonomyLevel, number> = {
  L0: 0,
  L1: 1,
  L2: 2,
  L3: 3,
  L4: 4,
  L5: 5,
};

export const CHANNEL_REGIMES = ["mention-only", "open", "swarm"] as const;
export type ChannelRegime = (typeof CHANNEL_REGIMES)[number];

// The legal per-channel ceiling values (server: isValidChannelMaxAutonomyLevel
// rejects "L5" — L5/swarm is a REGIME + experimental flag, never a ceiling any
// cybo may be raised to). The ceiling picker only ever offers these five.
export const CHANNEL_CEILING_LEVELS = ["L0", "L1", "L2", "L3", "L4"] as const;

// Human labels for the FULL level ladder (knowledge/76-cybo-autonomy-levels.md
// §2's canonical ladder names) — used by the effective-level badge
// (ChannelDetailsDialog / the channel details route), the channel ceiling
// vocabulary, and Settings -> Autonomy's folded "Project team" section's
// Advanced per-agent editor (ProjectCyboRosterBody's cyboRow), which all
// reason about (or let an owner pick from) all 6 raw levels, not just the 4
// public presets below. This is the raw
// ladder, not the picker: L0/L3/L4's names here ("Silent"/"Proactive"/
// "Social/loop") deliberately differ from AUTONOMY_LEVEL_PRESETS' friendlier
// public names for the same ranks ("Off"/"Active"/"Autonomous") — the presets
// are the simplified public vocabulary, this is the precise one. L1's name is
// shared verbatim by both ("Mention-only") since there's no friendlier gloss
// needed. nearestAutonomyPreset below is the one place a raw level collapses
// to a preset for display.
export const AUTONOMY_LEVEL_LABELS: Record<AutonomyLevel, string> = {
  L0: "Silent",
  L1: "Mention-only",
  L2: "Listening",
  L3: "Proactive",
  L4: "Social/loop",
  L5: "Full agency",
};

// The 4 public presets surfaced by the Settings -> Autonomy segmented control
// — Off (L0) / Mention-only (L1) / Active (L3) / Autonomous (L4). L2
// ("Listening") and L5 ("Full agency") stay unsurfaced here (advanced/
// experimental — L2 is only reachable via Settings -> Autonomy's folded
// "Project team" section's Advanced full-ladder editor; L5 is a
// channel-regime-only experimental flag, never a per-cybo preset), same as
// the server list. L2+ (so L3/L4 here) needs Pro access server-side (see
// autonomyLevelRequiresPro); the pickers carry no Pro badge — a fresh workspace
// is in its trial, and the server's PRO_REQUIRED answer is the one gate.
export const AUTONOMY_LEVEL_PRESETS = [
  { level: "L0", label: "Off", hint: "Never acts on its own; still answers a direct mention" },
  {
    level: "L1",
    label: "Mention-only",
    hint: "Replies to mentions, DMs, and assigned or scheduled tasks",
  },
  { level: "L3", label: "Active", hint: "May speak when relevant" },
  { level: "L4", label: "Autonomous", hint: "Picks up unanswered questions after 15 min" },
] as const satisfies ReadonlyArray<{ level: AutonomyLevel; label: string; hint: string }>;

// Maps ANY stored/resolved autonomy level (L0..L5) to the nearest of the 4
// AUTONOMY_LEVEL_PRESETS above, by rank — so a row holding an advanced level
// the public picker doesn't surface (L2/L5) still highlights a sensible option
// instead of matching none: L0 -> Off, L1 -> Mention-only, L2/L3 -> Active,
// L4/L5 -> Autonomous. Shared by the Settings -> Autonomy segmented control
// (and, historically, the Tasks project roster's own picker, before it became
// the full-ladder Advanced editor, now folded into Settings -> Autonomy's own
// "Project team" section — see ProjectCyboRosterBody), so a collapsed
// display can't drift between surfaces. This only governs how an EXISTING
// stored value DISPLAYS — writing a new value always goes straight through
// one of the 4 canonical levels (preset.level is already L0/L1/L3/L4), never
// through this mapping.
export function nearestAutonomyPreset(level: AutonomyLevel): AutonomyLevel {
  const rank = AUTONOMY_RANK[level];
  if (rank <= AUTONOMY_RANK.L0) return "L0";
  if (rank <= AUTONOMY_RANK.L1) return "L1";
  if (rank <= AUTONOMY_RANK.L3) return "L3";
  return "L4";
}

// A NEW agent's autonomy: the highest preset its creator can save, mirroring (never
// bypassing) cybo-privileged-fields.ts — L4 owner/admin-only, L2+ needs Pro access.
export function defaultNewAgentAutonomy(input: {
  role: string | null | undefined;
  proGated: boolean;
}): AutonomyLevel {
  if (input.proGated) return "L1";
  if (input.role != null && input.role !== "owner" && input.role !== "admin") return "L3";
  return "L4";
}

// NOTE: there is no per-channel regime picker (the "no per-channel autonomy
// regime" decision — feat/centralize-autonomy-regime) — CHANNEL_REGIME_META
// (the picker's labels/hints) was removed as dead code; it had already gone
// unused since the per-channel regime + ceiling Selects were pulled from the
// channel AI panel (a8124037f, "centralize autonomy into Settings ->
// Autonomy"). CHANNEL_REGIMES/ChannelRegime stay: channels.regime is still a
// real persisted DB column (untouched by this decision) and resolveEffectiveAutonomy
// below still threads it through for callers that read the raw dial.

// Behavior-neutral defaults (mirrors autonomy-dials.ts): an unset channel dial
// reproduces today's effective agency until an admin explicitly sets it —
// regime NULL -> open, maxLevel NULL -> L4. An unset cybo level NULL -> L1
// (the server's behaviorModeToLevel(undefined) fallback — resolveEffectiveAutonomy
// and normalizeCyboLevel below take an already-resolved level and default it
// straight to L1; a caller holding a raw cybo row instead uses
// currentAutonomyLevel() below, which walks the same behaviorModeToLevel
// fallback the server does).
const DEFAULT_CHANNEL_REGIME: ChannelRegime = "open";
const DEFAULT_CHANNEL_MAX_LEVEL: AutonomyLevel = "L4";
const DEFAULT_CYBO_LEVEL: AutonomyLevel = "L1";

function isAutonomyLevel(value: string | null | undefined): value is AutonomyLevel {
  return value != null && (AUTONOMY_LEVELS as readonly string[]).includes(value);
}

function isChannelRegime(value: string | null | undefined): value is ChannelRegime {
  return value != null && (CHANNEL_REGIMES as readonly string[]).includes(value);
}

// Normalize a possibly null/undefined/garbage stored autonomy level (e.g.
// cybo.autonomyLevel straight off the wire) to a concrete AutonomyLevel, using
// the same L1 default as resolveEffectiveAutonomy's cyboLevel fallback. Lets a
// caller (the effective-level badge) compare the cybo's OWN level against its
// effective/capped level with identical fallback semantics on both sides.
export function normalizeCyboLevel(value: string | null | undefined): AutonomyLevel {
  return isAutonomyLevel(value) ? value : DEFAULT_CYBO_LEVEL;
}

// Migrate the deprecated behaviorMode -> an autonomy level (responsive/unset ->
// L1, proactive -> L3). Mirrors the server's behaviorModeToLevel
// (autonomy-types.ts) value-for-value.
export function behaviorModeToLevel(mode: string | null | undefined): AutonomyLevel {
  return mode === "proactive" ? "L3" : "L1";
}

// A cybo's current autonomy level for display: its own persisted level, or the
// behaviorModeToLevel fallback for an un-migrated row (autonomyLevel null) OR
// an invalid/corrupt one (isAutonomyLevel false) — same degrade-don't-throw
// strategy as resolveEffectiveAutonomy, so a garbage string can never leak
// past this function as if it were a real level. Shared by the Settings ->
// Autonomy per-cybo dial and the Tasks project roster's preview strip so the
// two pickers can't drift on how they read the "current" level off a raw
// cybo row.
export function currentAutonomyLevel(cybo: {
  autonomyLevel?: string | null;
  behaviorMode?: string | null;
}): AutonomyLevel {
  return isAutonomyLevel(cybo.autonomyLevel)
    ? cybo.autonomyLevel
    : behaviorModeToLevel(cybo.behaviorMode);
}

export interface ResolveEffectiveAutonomyInput {
  // The cybo's own dial (cybo.autonomyLevel). Null/undefined/garbage -> L1.
  cyboLevel: string | null | undefined;
  // The channel's regime. Null/undefined/garbage -> "open".
  channelRegime: string | null | undefined;
  // The channel's ceiling on a cybo's disposition here. Null/undefined/garbage -> "L4".
  channelMaxLevel: string | null | undefined;
}

export interface EffectiveAutonomy {
  effectiveLevel: AutonomyLevel;
  regime: ChannelRegime;
}

// Resolve a cybo's effective agency in a channel: the rank-min of its own dial
// and the channel's ceiling, plus the (defaulted) regime. Mirrors the server's
// resolveEffectiveAutonomy() value-for-value, including its defensive fallback
// for a corrupt/garbage persisted string (unrecognized values degrade to the
// same behavior-neutral defaults as null/undefined, never throw).
export function resolveEffectiveAutonomy(input: ResolveEffectiveAutonomyInput): EffectiveAutonomy {
  const cyboLevel = isAutonomyLevel(input.cyboLevel) ? input.cyboLevel : DEFAULT_CYBO_LEVEL;
  const regime = isChannelRegime(input.channelRegime)
    ? input.channelRegime
    : DEFAULT_CHANNEL_REGIME;
  const channelMaxLevel = isAutonomyLevel(input.channelMaxLevel)
    ? input.channelMaxLevel
    : DEFAULT_CHANNEL_MAX_LEVEL;

  const effectiveRank = Math.min(AUTONOMY_RANK[cyboLevel], AUTONOMY_RANK[channelMaxLevel]);
  const effectiveLevel = AUTONOMY_LEVELS[effectiveRank] ?? DEFAULT_CYBO_LEVEL;

  return { effectiveLevel, regime };
}
